import React, { Component } from 'react';
import { XIcon, CheckCircleIcon, ClockIcon, PackageIcon, XCircleIcon, CalendarIcon, UserIcon, MapPinIcon, CreditCardIcon, FileTextIcon, TruckIcon, ExternalLinkIcon, StoreIcon } from 'lucide-react';
interface PrescriptionItem {
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface TrackingEvent {
  status: string;
  timestamp: string;
  description: string;
}
interface PrescriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  prescription: {
    id: string;
    date: string;
    status: string;
    items: PrescriptionItem[];
    doctor?: string;
    deliveryAddress?: string;
    deliveryCoordinates?: {
      lat: number;
      lng: number;
    };
    pickupBranch?: {
      name: string;
      address: string;
      coordinates: {
        lat: number;
        lng: number;
      };
    };
    pickupTime?: string;
    paymentMethod?: string;
    total?: number;
    prescriptionSource?: 'cash' | 'wasfaty' | 'insurance' | 'outpatient' | 'yusr';
    source?: 'cash' | 'wasfaty' | 'insurance' | 'outpatient' | 'yusr';
    insuranceProvider?: string;
    wasfatyNumber?: string;
    externalReference?: string;
    trackingHistory?: TrackingEvent[];
  };
  onCancelRequest?: (referenceNumber: string) => void;
}
export function PrescriptionDetailsModal({
  isOpen,
  onClose,
  prescription,
  onCancelRequest
}: PrescriptionDetailsModalProps) {
  if (!isOpen) return null;
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-5084" data-mp="1baphhjquvf2d" />;
      case 'ready':
        return <PackageIcon className="w-6 h-6 text-blue-500" data-id="element-5085" data-mp="1gcojap6y63x7" />;
      case 'in-progress':
      case 'expired':
        return <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-5086" data-mp="1kcf8iogtsbzm" />;
      case 'insurance-rejected':
      default:
        return <XCircleIcon className="w-6 h-6 text-red-500" data-id="element-5087" data-mp="3i4t8hsx2an48" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for fulfillment';
      case 'in-progress':
        return 'Pharmacist review';
      case 'awaiting-approval':
        return 'Awaiting insurance approval';
      case 'out-for-delivery':
        return 'Out for delivery';
      case 'expired':
        return 'Prescription expired';
      case 'insurance-rejected':
        return 'Insurance claim rejected';
      case 'needs-contact':
        return 'Needs your attention';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Under review';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'ready':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'out-for-delivery':
        return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'in-progress':
      case 'awaiting-approval':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'expired':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'insurance-rejected':
      case 'needs-contact':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  const getPrescriptionSourceBadge = () => {
    const source = prescription.source ?? prescription.prescriptionSource;
    if (!source) return null;
    const badges = {
      cash: {
        label: 'Cash prescription',
        color: 'bg-gray-100 text-gray-700'
      },
      wasfaty: {
        label: 'Wasfaty',
        color: 'bg-blue-100 text-blue-700'
      },
      insurance: {
        label: 'Insurance',
        color: 'bg-green-100 text-green-700'
      },
      outpatient: {
        label: 'Outpatient clinic',
        color: 'bg-rose-100 text-rose-700'
      },
      yusr: {
        label: 'Yusr',
        color: 'bg-violet-100 text-violet-700'
      }
    };
    const badge = badges[source];
    return <div className="mb-4" data-id="element-5088" data-mp="17w0mea4wie4b">
        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${badge.color}`} data-id="element-5089" data-mp="17x2776bpyez8">
          <FileTextIcon className="w-3.5 h-3.5" data-id="element-5090" data-mp="1cxg884ewkonr" />
          {badge.label}
          {source === 'wasfaty' && prescription.wasfatyNumber && <span className="ml-1" data-id="element-5091" data-mp="zvcho2wz9063">• {prescription.wasfatyNumber}</span>}
          {source === 'insurance' && prescription.insuranceProvider && <span className="ml-1" data-id="element-5092" data-mp="v3kwy90i5onv">• {prescription.insuranceProvider}</span>}
          {(source === 'outpatient' || source === 'yusr') && prescription.externalReference && <span className="ml-1" data-id="element-5093" data-mp="m4z6zsspb7d2">• {prescription.externalReference}</span>}
        </span>
      </div>;
  };
  const getGoogleMapsLink = (coordinates?: {
    lat: number;
    lng: number;
  }, address?: string) => {
    if (coordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };
  return <div className="fixed inset-0 z-[200] flex items-end bg-black/45 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-label="Prescription details" data-id="element-5094" data-mp="22ifto0cucwsm">
      <button type="button" className="absolute inset-0" onClick={onClose} aria-label="Close prescription details" data-id="element-5095" data-mp="3pf5l56ouasqh" />
      <div className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-white text-left shadow-2xl sm:max-w-3xl sm:rounded-2xl" data-id="element-5096" data-mp="3u5vsuqkqn7oc">
        <div className="sticky top-0 z-20 bg-white px-6 pt-2 sm:hidden" data-id="element-5097" data-mp="24frc07ue3ph"><div className="mx-auto h-1 w-10 rounded-full bg-gray-300" data-id="element-5098" data-mp="31ii25a7mv8co" /></div>
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#1E2B55] px-6 py-4" data-id="element-5099" data-mp="36hkwo0pzlw6e">
            <div className="flex items-center justify-between" data-id="element-5100" data-mp="3aieov6agpx53">
              <div data-id="element-5101" data-mp="3ak99sklqhp96">
                <h3 className="text-xl font-bold text-white" data-id="element-5102" data-mp="3fh81kwhxdk47">
                  Prescription Details
                </h3>
                <p className="text-sm text-blue-100 mt-1" data-id="element-5103" data-mp="psfk15ops46p">{prescription.id}</p>
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" data-id="element-5104" data-mp="uqh8650zytp1">
                <XIcon className="w-6 h-6" data-id="element-5105" data-mp="2qsc32zwwk9uj" />
              </button>
            </div>
          </div>
          {/* Content */}
          <div className="px-6 py-6 space-y-6" data-id="element-5106" data-mp="30sb6yjidnc12">
            {/* Prescription Source Badge */}
            {getPrescriptionSourceBadge()}
            {/* Status */}
            <div className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(prescription.status)}`} data-id="element-5107" data-mp="2vu1ckb4h8fgi">
              <div className="flex items-center" data-id="element-5108" data-mp="2352rb5524x6z">
                {getStatusIcon(prescription.status)}
                <div className="ml-3" data-id="element-5109" data-mp="2htqk53niqst8">
                  <p className="font-bold" data-id="element-5110" data-mp="2hurwdbg9tlcj">
                    {getStatusText(prescription.status)}
                  </p>
                  <p className="text-sm" data-id="element-5111" data-mp="2d0xf0eo51igm">
                    {new Date(prescription.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </p>
                </div>
              </div>
              <CalendarIcon className="w-5 h-5" data-id="element-5112" data-mp="4mh9vofxaqa3" />
            </div>
            {/* Prescribed Items with Images and Prices */}
            <div data-id="element-5113" data-mp="3n1rr06anfpg9">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-5114" data-mp="3n2bbnuroorxz">
                <FileTextIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-5115" data-mp="3r3dawbllp3zy" />
                Prescribed Items
              </h4>
              <div className="space-y-3" data-id="element-5116" data-mp="inbh43r8il76">
                {prescription.items.map((item, index) => <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-5117" data-mp="nktqtwd2t4g8" data-mpi={index}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" data-id="element-5118" data-mp="ejlydux8qxu2" />
                    <div className="flex-1" data-id="element-5119" data-mp="2zbxg3u9w4ayn">
                      <h5 className="font-medium text-gray-900 mb-1" data-id="element-5120" data-mp="2z8t6uihkip14">
                        {item.name}
                      </h5>
                      <p className="text-sm text-gray-600" data-id="element-5121" data-mp="3lze585u9rnc7">
                        Quantity: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5122" data-mp="3h2dxzrlooeji">
                      <p className="text-lg font-bold text-[#1E2B55]" data-id="element-5123" data-mp="3c2htlr92ql3f">
                        SAR {item.price.toFixed(2)}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Doctor Info */}
            {prescription.doctor && <div data-id="element-5124" data-mp="3sg70jivocwm">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-5125" data-mp="3roe5ggo0ta1">
                  <UserIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-5126" data-mp="3v028q6m1e5au" />
                  Prescribed By
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-5127" data-mp="wc5548ygaoi2">
                  <p className="text-sm text-gray-700" data-id="element-5128" data-mp="ritvy1661vmz">{prescription.doctor}</p>
                </div>
              </div>}
            {/* Delivery Address or Pickup Branch */}
            {prescription.deliveryAddress && <div data-id="element-5129" data-mp="13vo2xlccsmap">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-5130" data-mp="18t7g0hxbet12">
                  <MapPinIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-5131" data-mp="18vadto4plqgc" />
                  Delivery Address
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-5132" data-mp="1hu5opdip7opz">
                  <p className="text-sm text-gray-700 mb-3" data-id="element-5133" data-mp="1msfipksosja0">
                    {prescription.deliveryAddress}
                  </p>
                  {getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress) && <a href={getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-5134" data-mp="21hv8oyke8kon">
                      <MapPinIcon className="w-4 h-4" data-id="element-5135" data-mp="3gx6hjw4vpi3j" />
                      View on Google Maps
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-5136" data-mp="3lpgf9yc8jmgd" />
                    </a>}
                </div>
              </div>}
            {prescription.pickupBranch && <div data-id="element-5137" data-mp="3uo1qt9jai17n">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-5138" data-mp="3unjr22xf54uz">
                  <StoreIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-5139" data-mp="3jl8qm5zqihr" />
                  Pickup Branch
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-5140" data-mp="8g2686z2y97i">
                  <p className="font-medium text-gray-900 mb-1" data-id="element-5141" data-mp="25c9f34dxlm1r">
                    {prescription.pickupBranch.name}
                  </p>
                  <p className="text-sm text-gray-700 mb-3" data-id="element-5142" data-mp="22pqklv5hucwj">
                    {prescription.pickupBranch.address}
                  </p>
                  {prescription.pickupTime && <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg" data-id="element-5143" data-mp="1t0706l24uazk">
                      <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-5144" data-mp="1sxuj0a4784nk" />
                      <span className="text-sm font-medium text-blue-900" data-id="element-5145" data-mp="1o2no9rswqaas">
                        Pickup Time: {prescription.pickupTime}
                      </span>
                    </div>}
                  <a href={getGoogleMapsLink(prescription.pickupBranch.coordinates, prescription.pickupBranch.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-5146" data-mp="2gqjkkveke071">
                    <MapPinIcon className="w-4 h-4" data-id="element-5147" data-mp="3jidu9e0g3wqq" />
                    View on Google Maps
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-5148" data-mp="3njz4erllhwx8" />
                  </a>
                </div>
              </div>}
            {/* Tracking History or Pickup Time */}
            {prescription.trackingHistory && prescription.trackingHistory.length > 0 && <div data-id="element-5149" data-mp="3el3w99yzqcra">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-5150" data-mp="2ztv34qvvq99e">
                    <TruckIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-5151" data-mp="34l3cxo14fwn3" />
                    Delivery Tracking
                  </h4>
                  <div className="space-y-4" data-id="element-5152" data-mp="2qurihih2dlss">
                    {prescription.trackingHistory.map((event, index) => <div key={index} className="flex gap-4" data-id="element-5153" data-mp="2vtb4fivnqxl5" data-mpi={index}>
                        <div className="flex flex-col items-center" data-id="element-5154" data-mp="2gv0qbnfz7ivv">
                          <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5155" data-mp="2lulymoh1d3l9" />
                          {index < prescription.trackingHistory.length - 1 && <div className="w-0.5 h-full bg-gray-300 mt-1" data-id="element-5156" data-mp="1y8johqmxbvsk" />}
                        </div>
                        <div className="flex-1 pb-4" data-id="element-5157" data-mp="2c4wvg5tu0ess">
                          <div className="flex items-center justify-between mb-1" data-id="element-5158" data-mp="276vcdkweofhb">
                            <p className={`font-medium ${index === 0 ? 'text-green-700' : 'text-gray-700'}`} data-id="element-5159" data-mp="2l767x18nui8t">
                              {event.status}
                            </p>
                            <span className="text-xs text-gray-500" data-id="element-5160" data-mp="2uwpn9wx5ztyx">
                              {new Date(event.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-5161" data-mp="16zgh31l40lqu">
                            {event.description}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}
            {/* Payment Method */}
            {prescription.paymentMethod && <div data-id="element-5162" data-mp="jbyxvufwuvzh">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-5163" data-mp="o9iayr0vh2pu">
                  <CreditCardIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-5164" data-mp="obl8rx89o054" />
                  Payment Method
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-5165" data-mp="1zox8bvwztwlh">
                  <p className="text-sm text-gray-700" data-id="element-5166" data-mp="1zqi0xfzqz5gp">
                    {prescription.paymentMethod}
                  </p>
                </div>
              </div>}
            {/* Total */}
            {prescription.total && <div className="border-t border-gray-200 pt-4" data-id="element-5167" data-mp="3k66br6qj2bz4">
                <div className="flex items-center justify-between" data-id="element-5168" data-mp="3k8slnd0w53ki">
                  <span className="text-lg font-bold text-gray-900" data-id="element-5169" data-mp="3fe55c1g1jfh0">Total</span>
                  <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-5170" data-mp="3aflebmmlbdhs">
                    SAR {prescription.total.toFixed(2)}
                  </span>
                </div>
              </div>}
          </div>
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex gap-3 sticky bottom-0" data-id="element-5171" data-mp="31ib4m5pf7wez">
            <button onClick={onClose} className="flex-1 bg-white border border-gray-300 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5172" data-mp="2wh3ysc6u97ay">
              Close
            </button>
            {prescription.status === 'in-progress' && onCancelRequest && <button type="button" onClick={() => onCancelRequest(prescription.id)} className="flex-1 rounded-lg border border-red-200 bg-white px-4 py-2.5 font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600" data-id="element-5173" data-mp="2k6mf7kp6hj7o">
                Cancel request
              </button>}
            {prescription.status === 'delivered' && <button className="flex-1 bg-pharmacy text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-150 hover:bg-pharmacy-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pharmacy" data-id="element-5174" data-mp="32v9efmkobedc">
                Reorder
              </button>}
            {prescription.status === 'expired' && <button type="button" onClick={() => window.location.assign('/prescription')} className="flex-1 rounded-lg bg-pharmacy px-4 py-2.5 font-medium text-white transition-colors duration-150 hover:bg-pharmacy-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pharmacy" data-id="element-5175" data-mp="1hipvok2j6t1y">
                Re-upload prescription
              </button>}
            {prescription.status === 'insurance-rejected' && <button type="button" onClick={() => window.location.assign('/prescription')} className="flex-1 rounded-lg bg-pharmacy px-4 py-2.5 font-medium text-white transition-colors duration-150 hover:bg-pharmacy-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pharmacy" data-id="element-5176" data-mp="djt8ajcw7d32">
                Choose payment method
              </button>}
          </div>
        </div>
    </div>;
}